import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { api } from '../contexts/AuthContext';

/**
 * Recuperação de palavra-passe da Lou's Way.
 * Usa a mesma instância de API que as outras páginas do site (prefixo /api).
 * Não coloca URLs fixos de Preview ou Produção no código.
 */
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    // A página explica o problema e não redireciona silenciosamente.
    if (!token) toast.error('O link de recuperação está incompleto. Pede um novo link.');
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token || loading || completed) return;
    if (password.length < 6) {
      toast.error('A palavra-passe deve ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('As palavras-passe não coincidem.');
      return;
    }
    if (new TextEncoder().encode(password).length > 72) {
      toast.error('A palavra-passe é demasiado longa.');
      return;
    }

    setLoading(true);
    try {
      // AuthContext já aponta para /api do ambiente atual.
      await api.post('/auth/reset-password-confirm', {
        token,
        new_password: password,
      });
      setCompleted(true);
      setPassword('');
      setConfirmPassword('');
      toast.success('Palavra-passe alterada com sucesso!');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(item => item.msg || 'Pedido inválido').join('; ')
          : 'Não foi possível alterar a palavra-passe. Tenta novamente.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="min-h-[100svh] bg-[#F9F8F6] flex items-center justify-center px-4 pt-24 pb-16 sm:pt-28"
      style={{ paddingBottom: 'max(64px, env(safe-area-inset-bottom, 0px))' }}
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md space-y-6 rounded-2xl border border-[#E6E2DA] bg-white p-5 shadow-xl sm:p-8">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1815]">Nova palavra-passe</h1>
          <p className="mt-2 text-sm text-[#635E54]">
            {completed ? 'A tua palavra-passe foi atualizada.' : 'Escolhe a tua nova palavra-passe de acesso à Lou’s Way.'}
          </p>
        </div>

        {!token ? (
          <div className="space-y-4 text-center" role="alert">
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900">
              <AlertCircle className="h-5 w-5 shrink-0" />
              O link de recuperação está incompleto ou inválido. Solicita um novo link na página de início de sessão.
            </div>
            <Button type="button" onClick={() => navigate('/login', { replace: true })}
              className="h-12 w-full rounded-full bg-[#1A1815] text-white">
              Voltar ao início de sessão
            </Button>
          </div>
        ) : completed ? (
          <div className="space-y-4 text-center" role="status">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
            <p className="text-sm text-[#635E54]">Já podes entrar com a nova palavra-passe.</p>
            <Button type="button" onClick={() => navigate('/login', { replace: true })}
              className="h-12 w-full rounded-full bg-[#BFA05F] text-white hover:bg-[#A68A4F]">
              Iniciar sessão
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="reset-new-password" className="block text-sm font-medium text-[#1A1815]">Nova palavra-passe</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-[#A8A29A]" />
                <Input
                  id="reset-new-password" name="new-password" autoComplete="new-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Pelo menos 6 caracteres" minLength={6}
                  className="h-12 pl-10 pr-12 text-base" value={password}
                  onChange={event => setPassword(event.target.value)} required
                />
                <button type="button" onClick={() => setShowPassword(value => !value)}
                  aria-label={showPassword ? 'Ocultar palavra-passe' : 'Mostrar palavra-passe'}
                  className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center text-[#635E54]"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="reset-confirm-password" className="block text-sm font-medium text-[#1A1815]">Confirmar palavra-passe</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-[#A8A29A]" />
                <Input id="reset-confirm-password" name="confirm-password" autoComplete="new-password"
                  type={showPassword ? 'text' : 'password'} placeholder="Repete a nova palavra-passe"
                  minLength={6} className="h-12 pl-10 text-base" value={confirmPassword}
                  onChange={event => setConfirmPassword(event.target.value)} required />
              </div>
            </div>
            <Button type="submit" disabled={loading}
              className="h-12 w-full rounded-full bg-[#BFA05F] font-bold text-white hover:bg-[#A68A4F] disabled:opacity-60">
              {loading ? 'A guardar…' : 'Confirmar nova palavra-passe'}
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}
